import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FaEye } from "react-icons/fa";
import { toast } from "react-toastify";
import {
  fetchVendorShopOrderDetail,
  initiateVendorShopRefund,
  nextVendorSuborderStatusUpdate,
  orderRowShowsDispatch,
  orderRowShowsRefundInitiate,
  orderRowShowsReturnActions,
  updateVendorShopSuborderStatus,
  formatVendorRefundStatusLabel,
  formatVendorReturnStatusLabel,
} from "../../utils/vendorShopOrders";
import ShopReturnActionModal from "./ShopReturnActionModal";
import ShopReturnDetailSection from "./ShopReturnDetailSection";

const PLACEHOLDER_IMG =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='48' height='48'%3E%3Crect fill='%23E1E8D5' width='48' height='48' rx='4'/%3E%3C/svg%3E";

const actionBtnClass =
  "w-9 h-9 inline-flex items-center justify-center bg-[#d7e1cb] border border-[#b8c4a8] text-[#2B2B2B] hover:bg-[#c8d4b8] transition-colors rounded-sm";

const defaultOrder = {
  orderId: "8745367836HJKKJ",
  orderDateAndTime: "14/10/2022 - 10:00 PM",
  orderStatus: "New Order",
  paymentType: "UPI",
  netPriceAmount: "20.00",
  netAmount: "25.00",
  customerName: "Patrick Bateman",
  customerMobile: "+1 1234567890",
  city: "Gurgaon",
  email: "Patrick@Mail.Com",
  items: [
    {
      id: 1,
      name: "Coca-Cola",
      title: "Coca-Cola Classic",
      image: PLACEHOLDER_IMG,
      quantity: 5,
      size: "300 Ml",
      price: "25.00",
      tax: "5%",
      sku: "COKE-300",
      category: "Beverages",
      subCategory: "Soft Drinks",
      brand: "Coca-Cola",
      color: "Red",
      coupons: "SOFT10",
      discount: "5%",
      deliveryPincode: "122001",
      gender: "Unisex",
      description:
        "Refreshing classic Coca-Cola soft drink, ideal for events and catering.",
    },
    {
      id: 2,
      name: "Red Bull",
      title: "Red Bull Energy Drink",
      image: PLACEHOLDER_IMG,
      quantity: 7,
      size: "300 Ml",
      price: "25.00",
      tax: "5%",
      sku: "RB-300",
      category: "Beverages",
      subCategory: "Energy Drinks",
      brand: "Red Bull",
      color: "Blue",
      coupons: "ENERGY5",
      discount: "10%",
      deliveryPincode: "122001",
      gender: "Unisex",
      description:
        "Red Bull energy drink for high-energy events and late-night functions.",
    },
  ],
};

function DetailRow({ label, value, highlight }) {
  return (
    <div className="flex justify-between items-center gap-4 px-6 py-3 border-b border-[#E1E8D5] text-[13px] font-Montserratmedium">
      <span className="text-[#555555] shrink-0">{label}</span>
      <span
        className={`text-right ${
          highlight ? "text-[#F4802C]" : "text-[#2B2B2B]"
        }`}
      >
        {value || "—"}
      </span>
    </div>
  );
}

function SectionCard({ title, children }) {
  return (
    <div className="flex-1 min-w-0">
      <p className="text-[13px] text-[#F4802C] mb-2 font-Montserratmedium">
        {title}
      </p>
      <div className="bg-[#A4B79215]">{children}</div>
    </div>
  );
}

const ViewOrder = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isRent = location.pathname.includes("/rent");
  const listBase = isRent
    ? "/merchant-management/rent"
    : "/merchant-management/shop";
  const shopOrderId = String(
    location.state?.order?.shopOrderId ||
      location.state?.shopOrderId ||
      location.state?.order?.id ||
      ""
  ).trim();
  const [order, setOrder] = useState(() =>
    isRent
      ? { ...defaultOrder, ...(location.state?.order || {}) }
      : location.state?.order || null
  );
  const [loading, setLoading] = useState(!isRent && Boolean(shopOrderId));
  const [error, setError] = useState("");
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const statusGroup = String(location.state?.statusGroup || "").trim();

  const showReturnActions =
    !isRent &&
    Boolean(order) &&
    orderRowShowsReturnActions(order, statusGroup);
  const fulfillmentNext =
    !isRent && order ? nextVendorSuborderStatusUpdate(order) : null;
  const showDispatch =
    !isRent &&
    Boolean(order) &&
    orderRowShowsDispatch(order, statusGroup || "ongoing");
  const showRefundInitiate =
    !isRent && Boolean(order) && orderRowShowsRefundInitiate(order);

  useEffect(() => {
    if (isRent || !shopOrderId) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      const result = await fetchVendorShopOrderDetail(shopOrderId);
      if (cancelled) return;
      if (result.ok && result.order) {
        setOrder(result.order);
        setError("");
      } else {
        setError(result.message || "Could not load order");
        if (!order) setOrder(location.state?.order || null);
      }
      setLoading(false);
    };
    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRent, shopOrderId]);

  const formatMoney = (val) => {
    if (val == null || val === "") return "—";
    const s = String(val).trim();
    if (s.startsWith("$") || s.startsWith("₹")) return s;
    return isRent ? `$${s}` : `₹ ${s}`;
  };

  const goViewProduct = (item) => {
    const productId = String(item.productId || item.id || "").trim();
    if (!productId) return;
    navigate(`${listBase}/view-order-product`, {
      state: {
        productId,
        fromShopOrder: true,
        returnToOrder: true,
        order,
        shopOrderId: order?.shopOrderId || shopOrderId,
        statusGroup: location.state?.statusGroup || "",
        listMode: location.state?.listMode || (isRent ? "rent" : "shop"),
        listingMode: isRent ? "rent" : "shop",
        orderLineItem: {
          sizeId: item.sizeId,
          colorId: item.colorId,
          size: item.size,
          color: item.color,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          totalItemPrice: item.totalItemPrice,
          discount: item.discount,
          title: item.title || item.name,
        },
      },
    });
  };

  const reloadOrder = async () => {
    if (!shopOrderId || isRent) return;
    const result = await fetchVendorShopOrderDetail(shopOrderId);
    if (result.ok && result.order) setOrder(result.order);
  };

  const handleDispatch = async () => {
    if (!shopOrderId || actionBusy || !fulfillmentNext) return;
    setActionBusy(true);
    const result = await updateVendorShopSuborderStatus(shopOrderId, {
      orderStatus: fulfillmentNext.orderStatus,
    });
    setActionBusy(false);
    if (result.ok) {
      toast.success(result.message || fulfillmentNext.successMessage);
      reloadOrder();
      return;
    }
    toast.error(result.message || "Could not update order status");
  };

  const handleRefundInitiate = async () => {
    if (!shopOrderId || actionBusy) return;
    setActionBusy(true);
    const result = await initiateVendorShopRefund(shopOrderId);
    setActionBusy(false);
    if (result.ok) {
      toast.success(result.message || "Refund initiated");
      reloadOrder();
      return;
    }
    toast.error(result.message || "Could not initiate refund");
  };

  const fromCancelledList = statusGroup === "cancelled";
  const fromReturnsList = statusGroup === "returns";

  const returnDetail =
    order?.returnRequest && typeof order.returnRequest === "object"
      ? order.returnRequest
      : null;

  return (
    <div className="pb-8">
      <div className="flex items-center justify-between gap-4 mb-8">
        <button
          type="button"
          onClick={() => navigate(listBase)}
          className="text-[13px] font-Montserratmedium text-[#F4802C] border border-[#F4802C] px-6 py-2 bg-white hover:bg-[#FFF8F3] min-h-[42px]"
        >
          Back
        </button>
        <div className="flex flex-wrap gap-2 justify-end">
          {showDispatch ? (
            <button
              type="button"
              disabled={actionBusy}
              onClick={handleDispatch}
              className="text-[13px] font-Montserratmedium text-white bg-[#2B2B2B] border border-[#2B2B2B] px-6 py-2 hover:bg-[#444] min-h-[42px] disabled:opacity-50"
            >
              {fulfillmentNext?.actionLabel || "Update status"}
            </button>
          ) : null}
          {showReturnActions ? (
            <button
              type="button"
              onClick={() => setShowReturnModal(true)}
              className="text-[13px] font-Montserratmedium text-white bg-[#A4B792] border border-[#A4B792] px-6 py-2 hover:bg-[#95a882] min-h-[42px]"
            >
              Return action
            </button>
          ) : null}
          {showRefundInitiate ? (
            <button
              type="button"
              disabled={actionBusy}
              onClick={handleRefundInitiate}
              className="text-[13px] font-Montserratmedium text-white bg-[#F4802C] border border-[#F4802C] px-6 py-2 hover:bg-[#e06f1f] min-h-[42px] disabled:opacity-50"
            >
              Initiate refund
            </button>
          ) : null}
        </div>
      </div>

      {loading ? (
        <p className="text-center text-[13px] font-Montserratmedium text-[#555555] py-16">
          Loading order...
        </p>
      ) : !order ? (
        <p className="text-center text-[13px] font-Montserratmedium text-[#555555] py-16">
          {error || "Order not found."}
        </p>
      ) : (
      <>
      {!fromCancelledList && returnDetail ? (
        <ShopReturnDetailSection
          returnRequest={returnDetail}
          returnStatusLabel={formatVendorReturnStatusLabel(order.returnStatus)}
        />
      ) : null}
      <div className="flex flex-col lg:flex-row gap-6 mb-10">
        <SectionCard title="Order Details">
          <DetailRow label="Order ID" value={order.orderId} />
          {order.suborderNumber ? (
            <DetailRow label="Suborder ID" value={order.suborderNumber} />
          ) : null}
          <DetailRow
            label="Order Date & Time"
            value={order.orderDateAndTime}
          />
          <DetailRow
            label="Status"
            value={
              order.statusActionLabel ||
              (fromCancelledList && order.masterOrderStatusLabel) ||
              order.orderStatus
            }
            highlight
          />
          {order.cancelledActionLabel ? (
            <DetailRow label="Cancellation" value={order.cancelledActionLabel} />
          ) : null}
          {order.refundActionLabel ? (
            <DetailRow label="Refund" value={order.refundActionLabel} />
          ) : null}
          <DetailRow label="Payment" value={order.paymentType} />
          {!fromCancelledList ? (
            <DetailRow
              label="Return status"
              value={formatVendorReturnStatusLabel(order.returnStatus)}
              highlight={
                formatVendorReturnStatusLabel(order.returnStatus) !== "—"
              }
            />
          ) : null}
          {fromReturnsList ? (
            <DetailRow
              label="Refund status"
              value={formatVendorRefundStatusLabel(order.refundStatus, {
                returnsContext: true,
              })}
            />
          ) : String(order.refundStatus || "").toLowerCase() &&
            String(order.refundStatus || "").toLowerCase() !== "none" ? (
            <DetailRow
              label="Refund status"
              value={formatVendorRefundStatusLabel(order.refundStatus)}
            />
          ) : null}
          {String(order.refundStatus || "").toLowerCase() === "failed" &&
          order.refundError ? (
            <DetailRow label="Refund error" value={order.refundError} highlight />
          ) : null}
          {order.statusActionLabel ? (
            <DetailRow label="Last action" value={order.statusActionLabel} />
          ) : null}
          {order.refundAmount > 0 ? (
            <DetailRow
              label="Refund amount"
              value={`₹ ${Number(order.refundAmount).toLocaleString("en-IN")}`}
            />
          ) : null}
          <DetailRow
            label="Total MRP"
            value={formatMoney(order.netPriceAmount)}
          />
          <DetailRow
            label="Vendor Item Total"
            value={formatMoney(order.netAmount)}
          />
          {order.paidAmount && order.paidAmount !== "0.00" ? (
            <DetailRow label="Paid Amount" value={formatMoney(order.paidAmount)} />
          ) : null}
          {order.couponCode && order.couponCode !== "—" ? (
            <DetailRow label="Coupon" value={order.couponCode} />
          ) : null}
        </SectionCard>

        <SectionCard title="Customer Details">
          <DetailRow label="Customer Name" value={order.customerName} />
          <DetailRow
            label="Customer Mobile Number"
            value={order.customerMobile}
          />
          <DetailRow label="Delivery Address" value={order.addressLine} />
          <DetailRow label="City" value={order.city} />
          <DetailRow label="State" value={order.state} />
          <DetailRow label="Pincode" value={order.pincode} />
          <DetailRow
            label="Email Address"
            value={order.email && order.email !== "—" ? order.email : "—"}
          />
        </SectionCard>
      </div>

      <div>
        <p className="text-[13px] text-[#F4802C] mb-3 font-Montserratmedium">
          Items
        </p>
        <div className="overflow-x-auto bg-white">
          <table className="w-full min-w-[720px] border-collapse module-data-table">
            <thead>
              <tr className="bg-[#d7e1cb]">
                {[
                  "S.No",
                  "Image",
                  "Products Name",
                  "Quantity",
                  "Size",
                  "Color",
                  "Discount",
                  "Price",
                  "Action",
                ].map((h) => (
                  <th
                    key={h}
                    className="px-4 py-3 text-center text-[14px] font-BonaNovabold text-[#2E2E2E] whitespace-nowrap"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(order.items || []).map((item, i) => (
                <tr
                  key={item.id ?? i}
                  className={`border-b border-[#E5E7EB] ${
                    i % 2 === 0 ? "bg-white" : "bg-[#F9FAF7]"
                  }`}
                >
                  <td className="px-4 py-3 text-center text-[12px] font-sf-pro text-[#24272C]">
                    {i + 1}
                  </td>
                  <td className="px-4 py-2 text-center">
                    <img
                      src={item.image || PLACEHOLDER_IMG}
                      alt={item.name || "Product"}
                      className="w-12 h-12 object-cover rounded border border-[#E5E7EB] mx-auto"
                    />
                  </td>
                  <td className="px-4 py-3 text-center text-[12px] font-sf-pro text-[#24272C]">
                    {item.name}
                  </td>
                  <td className="px-4 py-3 text-center text-[12px] font-sf-pro text-[#24272C]">
                    {item.quantity}
                  </td>
                  <td className="px-4 py-3 text-center text-[12px] font-sf-pro text-[#24272C]">
                    {item.size || "—"}
                  </td>
                  <td className="px-4 py-3 text-center text-[12px] font-sf-pro text-[#24272C]">
                    {item.color || "—"}
                  </td>
                  <td className="px-4 py-3 text-center text-[12px] font-sf-pro text-[#24272C]">
                    {item.discount || "—"}
                  </td>
                  <td className="px-4 py-3 text-center text-[12px] font-sf-pro text-[#24272C]">
                    {formatMoney(item.price)}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button
                      type="button"
                      className={actionBtnClass}
                      aria-label={`View ${item.name}`}
                      onClick={() => goViewProduct(item)}
                    >
                      <FaEye className="text-sm" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      </>
      )}

      <ShopReturnActionModal
        open={showReturnModal}
        order={order}
        shopOrderId={shopOrderId}
        onClose={() => setShowReturnModal(false)}
        onSuccess={() => {
          reloadOrder();
          setShowReturnModal(false);
        }}
      />
    </div>
  );
};

export default ViewOrder;
